import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { QueueMusic } from '@mui/icons-material';

const RUNDOWN_POLL_MS = 15000;

const clockAt = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const categoryLabel = (item) => {
  if (item.type === 'voicetrack' || item.type === 'voice') return 'Voicetrack';
  if (item.type === 'ai_voicetrack') return 'AI VT';
  if (item.type === 'commercial_break') return 'Ads';
  if (item.type === 'stream_relay') return 'Relay';
  return 'Music';
};

const categoryColor = (item) => {
  if (item.type === 'voicetrack' || item.type === 'voice' || item.type === 'ai_voicetrack') return '#a855f7';
  if (item.type === 'commercial_break') return '#f59e0b';
  if (item.type === 'stream_relay') return '#64748b';
  return '#22d3ee';
};

/**
 * Read-only rundown for an invited co-presenter.
 *
 * Deliberately narrow: only what a presenter in the room needs (order, titles,
 * timings and where the voicetrack slot falls). No asset ids, file paths or ad
 * detail, because the link is held by someone outside the station's account.
 */
export default function GuestRundown({ token, api }) {
  const [rundown, setRundown] = useState(null);
  const [error, setError] = useState('');

  // The host may load a log, or move to another marker, after the guest opens
  // the link, so the rundown is re-read while it is on screen.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const payload = await api.getInviteRundown(token);
        if (cancelled) return;
        setRundown(payload || null);
        setError('');
      } catch (requestError) {
        if (!cancelled) setError(requestError?.response?.data?.message || '');
      }
    };
    load();
    const interval = setInterval(load, RUNDOWN_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [api, token]);

  const heading = useMemo(() => {
    if (!rundown?.playlist_date) return 'This hour';
    const hour = String(rundown.playlist_hour ?? 0).padStart(2, '0');
    return `${rundown.playlist_date} · ${hour}:00`;
  }, [rundown]);

  if (error) return null;
  if (!rundown || !Array.isArray(rundown.items) || rundown.items.length === 0) {
    return (
      <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <QueueMusic sx={{ color: 'var(--rs-accent)', fontSize: 20 }} />
            <Typography variant="subtitle2">The log</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            The host has not loaded a log for this hour yet. It will appear here once they do.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
      <CardContent sx={{ '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <QueueMusic sx={{ color: 'var(--rs-accent)', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ flex: 1 }}>The log</Typography>
          <Chip size="small" label={heading} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          What is scheduled this hour. Read-only — the host controls the running order.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 420, overflow: 'auto' }}>
          {rundown.items.map((item) => {
            const color = categoryColor(item);
            return (
              <Box
                key={`${item.index}-${item.title}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.6,
                  borderRadius: 1,
                  bgcolor: item.is_voicetrack ? 'rgba(168,85,247,0.12)' : 'var(--rs-surface)',
                  border: '1px solid',
                  borderColor: item.is_voicetrack ? 'rgba(168,85,247,0.4)' : 'var(--rs-border)',
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  className="tabular-nums"
                  sx={{ width: 42, color: 'var(--rs-muted)', flexShrink: 0 }}
                >
                  {clockAt(item.offset_seconds)}
                </Typography>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: item.is_voicetrack ? 700 : 500 }}>
                    {item.title}
                  </Typography>
                  {item.artist ? (
                    <Typography variant="caption" noWrap sx={{ color: 'var(--rs-muted)', display: 'block' }}>
                      {item.artist}
                    </Typography>
                  ) : null}
                </Box>
                <Chip
                  size="small"
                  label={categoryLabel(item)}
                  sx={{
                    bgcolor: `${color}22`,
                    color,
                    border: `1px solid ${color}55`,
                    fontSize: '0.6rem',
                    height: 18,
                  }}
                />
                <Typography
                  variant="caption"
                  className="tabular-nums"
                  sx={{ width: 36, textAlign: 'right', color: 'var(--rs-muted)', flexShrink: 0 }}
                >
                  {clockAt(item.duration)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}
